import classNames from 'classnames'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'

import i18n from 'i18n'
import {
  dismissNoticeForSession,
  dismissNoticeForToday,
  getMaintenanceNow,
} from 'services/maintenance'
import { confirmBase } from 'utils/ui'

import MaintenanceFrame from './MaintenanceFrame'
import styles from './style.module.scss'
import useI18nReady from './useI18nReady'

interface NoticeModalProps {
  url: string
  /** notice_period.modified_at — "오늘 하루 안보기" 식별자. */
  modifiedAt: string
  lang?: string
}

/**
 * 사전 공지(예고) 모달. confirmBase 로 표시(팝업 디자인 통일).
 * - footer 에 "오늘 하루 동안 안보기" 체크박스(좌) + 닫기 버튼(OkBtn, 우)만 렌더(cancel 미렌더).
 * - 명령형 모달이므로 mount 시 생성, unmount 시 destroy.
 */
function NoticeModal({ url, modifiedAt, lang }: NoticeModalProps): null {
  const dispatch = useDispatch()
  const { ready: i18nReady, revision: i18nRevision } = useI18nReady()

  useEffect(() => {
    if (!i18nReady) return undefined

    let dontShowToday = false

    const close = (): void => {
      // 닫기 시 항상 이번 세션 닫음으로 기록 → 인증 완료에 따른 재평가가 같은 로드 내에서 되살리지 않게 한다.
      dismissNoticeForSession(modifiedAt)
      if (dontShowToday) dismissNoticeForToday(modifiedAt, getMaintenanceNow())
      dispatch({ type: 'maintenance/SET_STATE', payload: { screen: { kind: 'none' } } })
    }

    const modal = confirmBase({
      className: classNames('mobile-fullpage'),
      content: (
        <div className={styles.modalContent} data-testid="maintenance-notice">
          <MaintenanceFrame
            url={url}
            title={i18n.t('maintenance.noticeTitle')}
            lang={lang}
            className={styles.modalFrame}
          />
        </div>
      ),
      okText: i18n.t('common.button.close'),
      okButtonProps: { 'data-testid': 'maintenance-notice-close' },
      footer: (_, { OkBtn }) => (
        <div className={styles.noticeFooter}>
          <label className={styles.dontShow}>
            <input
              type="checkbox"
              data-testid="maintenance-notice-dismiss"
              onChange={(e) => {
                dontShowToday = e.target.checked
              }}
            />
            {i18n.t('maintenance.dontShowToday')}
          </label>
          <OkBtn />
        </div>
      ),
      onOk: close,
      onCancel: close,
    })

    return () => modal.destroy()
  }, [url, modifiedAt, lang, dispatch, i18nReady, i18nRevision])

  return null
}

export default NoticeModal
